import { isIOS } from './utils/browser-detection.js';

const MEDIA_PLAY_REQUEST = 'mediaplayrequest';
const IMA_SDK_URL = 'https://imasdk.googleapis.com/js/sdkloader/ima3.js';
const IMA_SDK_SCRIPT_SELECTOR = 'script[data-bunny-ima-sdk="true"]';
const IMA_SDK_LOAD_TIMEOUT_MS = 10_000;

type BreakType = 'preroll' | 'midroll' | 'postroll';

type AdErrorPayload = {
  message: string;
  errorCode: number | null;
  vastErrorCode: number | null;
};

type AdBreakDetail = {
  cuePoint: number;
  breakType: BreakType;
  podIndex: number | null;
  adPosition: number | null;
  totalAds: number | null;
};

type CuePointsDetail = {
  cuePoints: number[];
};

type AdsControllerConfig = {
  adTagSrc?: string | null;
  contentSrc?: string | null;
  adTimeout?: number;
  skipDelay?: number;
};

type Constructor<TInstance extends object = object> = abstract new (
  ...args: never[]
) => TInstance;

type AdDisplayContainerTarget = HTMLElement | HTMLMediaElement;

type AdEventTypeMap = {
  CONTENT_PAUSE_REQUESTED: string;
  CONTENT_RESUME_REQUESTED: string;
  ALL_ADS_COMPLETED: string;
  STARTED: string;
  COMPLETE: string;
  SKIPPED: string;
  LOADED: string;
  CLICK: string;
  PAUSED: string;
  RESUMED: string;
};

type AdsManagerLoadedEventTypeMap = {
  ADS_MANAGER_LOADED: string;
};

type AdErrorEventTypeMap = {
  AD_ERROR: string;
};

interface ImaSdkSettings {
  setVpaidMode?(mode: unknown): void;
  setPlayerType?(playerType: string): void;
}

interface ImaSdkSettingsNamespace {
  VpaidMode?: {
    DISABLED: unknown;
  };
}

interface ImaAdPodInfo {
  getTimeOffset?(): number;
  getPodIndex?(): number;
  getAdPosition?(): number;
  getTotalAds?(): number;
}

interface ImaAd {
  getAdPodInfo?(): ImaAdPodInfo;
}

interface ImaAdEvent {
  type: string;
  getAd?(): ImaAd | null;
}

interface ImaErrorLike {
  message?: string;
  getMessage?(): string;
  getErrorCode?(): number;
  getVastErrorCode?(): number;
  getInnerError?(): unknown;
}

interface ImaAdErrorEvent {
  getError?(): ImaErrorLike;
}

interface ImaAdsRenderingSettings {
  restoreCustomPlaybackStateOnAdBreakComplete: boolean;
}

interface ImaAdsManager {
  addEventListener(type: string, listener: (event: unknown) => void): void;
  removeEventListener(type: string, listener: (event: unknown) => void): void;
  init(width: number, height: number, viewMode: string): void;
  start(): void;
  destroy?(): void;
  resize?(width: number, height: number, viewMode: string): void;
  discardAdBreak?(): void;
  setVolume?(volume: number): void;
  getCuePoints?(): unknown[];
  resume?(): void;
  pause?(): void;
}

interface ImaAdsManagerLoadedEvent {
  getAdsManager(
    contentPlayback: AdDisplayContainerTarget,
    settings: ImaAdsRenderingSettings,
  ): ImaAdsManager;
}

interface ImaAdsLoader {
  addEventListener(type: string, listener: (event: unknown) => void): void;
  removeEventListener(type: string, listener: (event: unknown) => void): void;
  requestAds(request: ImaAdsRequest): void;
  contentComplete?(): void;
  destroy?(): void;
}

interface ImaAdsRequest {
  adTagUrl?: string;
  linearAdSlotWidth?: number;
  linearAdSlotHeight?: number;
  nonLinearAdSlotWidth?: number;
  nonLinearAdSlotHeight?: number;
  vastLoadTimeout?: number;
  setAdWillAutoPlay?(value: boolean): void;
  setAdWillPlayMuted?(value: boolean): void;
}

interface ImaAdDisplayContainer {
  initialize?(): void;
  destroy?(): void;
}

interface ImaNamespace {
  settings?: ImaSdkSettings;
  ImaSdkSettings?: ImaSdkSettingsNamespace;
  ViewMode: {
    NORMAL: string;
    FULLSCREEN: string;
  };
  AdDisplayContainer: new (
    container: HTMLElement,
    videoElement: AdDisplayContainerTarget,
  ) => ImaAdDisplayContainer;
  AdsLoader: new (adDisplayContainer: ImaAdDisplayContainer) => ImaAdsLoader;
  AdsRequest: new () => ImaAdsRequest;
  AdsRenderingSettings: new () => ImaAdsRenderingSettings;
  AdsManagerLoadedEvent: {
    Type: AdsManagerLoadedEventTypeMap;
  };
  AdErrorEvent: {
    Type: AdErrorEventTypeMap;
  };
  AdEvent: {
    Type: AdEventTypeMap;
  };
}

declare global {
  interface Window {
    google?: {
      ima?: ImaNamespace;
    };
  }
}

export interface AdTagControllerHost extends HTMLElement {
  autoplay?: boolean;
  muted?: boolean;
  volume?: number;
  ended?: boolean;
  duration?: number;
  currentTime?: number;
  nativeEl?: HTMLMediaElement | null;
  play(): Promise<unknown>;
  pause?(): void;
  connectedCallback?(): void;
  disconnectedCallback?(): void;
  attributeChangedCallback?(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void;
  _playContentInternal(): Promise<unknown>;
  _playContentBypass(): Promise<unknown>;
  _consumePlayBypass?(): boolean;
  _isPlayBypassed?(): boolean;
}

let imaSdkPromise: Promise<ImaNamespace> | null = null;

function getIma(): ImaNamespace | null {
  const globalScope = globalThis as typeof globalThis & {
    google?: {
      ima?: ImaNamespace;
    };
  };
  return globalThis.window?.google?.ima ?? globalScope.google?.ima ?? null;
}

function markImaSdkScriptState(
  script: HTMLScriptElement,
  state: 'loading' | 'loaded' | 'failed',
): void {
  script.dataset.bunnyImaSdk = 'true';
  script.dataset.bunnyImaSdkState = state;
}

function waitForImaSdk(script: HTMLScriptElement): Promise<ImaNamespace> {
  return new Promise<ImaNamespace>((resolve, reject) => {
    let settled = false;
    let timeoutId: number | null = null;

    const cleanup = () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
    };

    const rejectLoad = (error: Error) => {
      if (settled) return;

      settled = true;
      markImaSdkScriptState(script, 'failed');
      cleanup();
      imaSdkPromise = null;
      reject(error);
    };

    const resolveLoad = () => {
      if (settled) return true;

      const loadedIma = getIma();
      if (!loadedIma) return false;

      settled = true;
      markImaSdkScriptState(script, 'loaded');
      cleanup();
      resolve(loadedIma);
      return true;
    };

    const handleLoad = () => {
      if (resolveLoad()) return;
      rejectLoad(new Error('IMA SDK loaded without google.ima'));
    };

    const handleError = () => {
      rejectLoad(new Error('Failed to load IMA SDK'));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });

    queueMicrotask(() => {
      resolveLoad();
    });

    timeoutId = globalThis.setTimeout(() => {
      if (resolveLoad()) return;
      rejectLoad(new Error('Timed out loading IMA SDK'));
    }, IMA_SDK_LOAD_TIMEOUT_MS);
  });
}

function loadImaSdk(): Promise<ImaNamespace> {
  const ima = getIma();
  if (ima) return Promise.resolve(ima);
  if (imaSdkPromise) return imaSdkPromise;

  let script = document.querySelector<HTMLScriptElement>(
    IMA_SDK_SCRIPT_SELECTOR,
  );
  if (
    script?.dataset.bunnyImaSdkState &&
    script.dataset.bunnyImaSdkState !== 'loading'
  ) {
    script.remove();
    script = null;
  }

  if (!script) {
    script = document.createElement('script');
    script.src = IMA_SDK_URL;
    script.async = true;
    markImaSdkScriptState(script, 'loading');
    document.head.appendChild(script);
  } else {
    markImaSdkScriptState(script, 'loading');
  }

  imaSdkPromise = waitForImaSdk(script).catch((error) => {
    imaSdkPromise = null;
    throw error;
  });

  return imaSdkPromise;
}

function normalizeCuePoints(cuePoints: unknown): number[] {
  if (!Array.isArray(cuePoints)) return [];

  return cuePoints
    .map((cuePoint) => Number(cuePoint))
    .filter((cuePoint) => Number.isFinite(cuePoint))
    .sort((left, right) => {
      if (left === -1) return 1;
      if (right === -1) return -1;
      return left - right;
    });
}

function cuePointToBreakType(cuePoint: number): BreakType {
  if (cuePoint === -1) return 'postroll';
  if (!Number.isFinite(cuePoint) || cuePoint <= 0) return 'preroll';
  return 'midroll';
}

function readPodInfoNumber(
  podInfo: ImaAdPodInfo | null,
  methodName: keyof ImaAdPodInfo,
): number | null {
  try {
    const method = podInfo?.[methodName];
    if (typeof method !== 'function') return null;
    const value = method.call(podInfo);
    return Number.isFinite(value) ? value : null;
  } catch (_error) {
    return null;
  }
}

function extractAdPodInfo(adEvent: unknown): {
  cuePoint: number | null;
  podIndex: number | null;
  adPosition: number | null;
  totalAds: number | null;
} {
  const getAd = (adEvent as ImaAdEvent | null)?.getAd;
  const ad = typeof getAd === 'function'
    ? getAd.call(adEvent as ImaAdEvent)
    : null;
  const podInfo = ad?.getAdPodInfo?.() ?? null;

  return {
    cuePoint: readPodInfoNumber(podInfo, 'getTimeOffset'),
    podIndex: readPodInfoNumber(podInfo, 'getPodIndex'),
    adPosition: readPodInfoNumber(podInfo, 'getAdPosition'),
    totalAds: readPodInfoNumber(podInfo, 'getTotalAds'),
  };
}

function isErrorLike(value: unknown): value is Error {
  return value instanceof Error;
}

function normalizeAdError(errorLike: unknown): {
  error: Error;
  message: string;
  errorCode: number | null;
  vastErrorCode: number | null;
  innerError: Error | null;
} {
  const getError = (errorLike as ImaAdErrorEvent | null)?.getError;
  const imaError = typeof getError === 'function'
    ? getError.call(errorLike as ImaAdErrorEvent)
    : errorLike;

  const getMessage = (imaError as ImaErrorLike | null)?.getMessage;
  const message = typeof getMessage === 'function'
    ? getMessage.call(imaError as ImaErrorLike)
    : typeof (imaError as ImaErrorLike | null)?.message === 'string'
    ? (imaError as ImaErrorLike).message as string
    : String(imaError || 'Unknown ad error');

  const getInnerError = (imaError as ImaErrorLike | null)?.getInnerError;
  const innerError = typeof getInnerError === 'function'
    ? getInnerError.call(imaError as ImaErrorLike)
    : null;

  const getErrorCode = (imaError as ImaErrorLike | null)?.getErrorCode;
  const getVastErrorCode = (imaError as ImaErrorLike | null)?.getVastErrorCode;

  return {
    error: isErrorLike(imaError) ? imaError : new Error(message),
    message,
    errorCode: typeof getErrorCode === 'function'
      ? getErrorCode.call(imaError as ImaErrorLike)
      : null,
    vastErrorCode: typeof getVastErrorCode === 'function'
      ? getVastErrorCode.call(imaError as ImaErrorLike)
      : null,
    innerError: isErrorLike(innerError) ? innerError : null,
  };
}

function buildAdsManagerEventTypes(ima: ImaNamespace): string[] {
  return [
    ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED,
    ima.AdEvent.Type.CONTENT_RESUME_REQUESTED,
    ima.AdEvent.Type.ALL_ADS_COMPLETED,
    ima.AdEvent.Type.STARTED,
    ima.AdEvent.Type.COMPLETE,
    ima.AdEvent.Type.SKIPPED,
    ima.AdEvent.Type.LOADED,
    ima.AdEvent.Type.CLICK,
    ima.AdEvent.Type.PAUSED,
    ima.AdEvent.Type.RESUMED,
  ];
}

export class AdTagController {
  readonly host: AdTagControllerHost;

  #adTagSrc: string | null = null;
  #contentSrc: string | null = null;
  #adTimeout = 5000;
  #connected = false;
  #controllerEl: HTMLElement | null = null;
  #playRequestPromise: Promise<unknown> | null = null;
  #imaReadyPromise: Promise<void> | null = null;
  #adsRequestPromise: Promise<ImaAdsManager> | null = null;
  #adsRequestResolve: ((adsManager: ImaAdsManager) => void) | null = null;
  #adsRequestReject: ((error: unknown) => void) | null = null;
  #adsLoader: ImaAdsLoader | null = null;
  #adsManager: ImaAdsManager | null = null;
  #adDisplayContainer: ImaAdDisplayContainer | null = null;
  #adDisplayInitialized = false;
  #cuePoints: number[] = [];
  #adsManagerStarted = false;
  #adsCompleted = false;
  #adBreakActive = false;
  #adPausedByClick = false;
  #currentBreakDetail: AdBreakDetail | null = null;
  #contentCompleteFired = false;
  #isSeeking = false;
  #seekSnapshotTime = 0;
  #lastKnownContentTime = 0;
  #contentTimeBeforeAdBreak: number | null = null;
  #requestContentResumeAfterBreak = false;
  #suppressContentPlayback = false;
  #adsBypassedForSession = false;
  #adOverlay: HTMLDivElement;
  #adContainer: HTMLDivElement;

  readonly #onMediaPlayRequest: (event: Event) => void;
  readonly #onContentPlay: () => void;
  readonly #onContentPlaying: () => void;
  readonly #onContentEnded: () => void;
  readonly #onContentSeeking: () => void;
  readonly #onContentSeeked: () => void;
  readonly #onContentVolumeChange: () => void;
  readonly #onWindowResize: () => void;
  readonly #onAdsManagerLoaded: (event: unknown) => void;
  readonly #onAdsLoaderError: (event: unknown) => void;
  readonly #onAdsManagerError: (event: unknown) => void;
  readonly #onAdEvent: (event: unknown) => void;
  readonly #onVisibilityChange: () => void;

  constructor(host: AdTagControllerHost) {
    this.host = host;

    this.#onMediaPlayRequest = this.#handleMediaPlayRequest.bind(this);
    this.#onContentPlay = this.#handleContentPlay.bind(this);
    this.#onContentPlaying = this.#handleContentPlaying.bind(this);
    this.#onContentEnded = this.#handleContentEnded.bind(this);
    this.#onContentSeeking = this.#handleContentSeeking.bind(this);
    this.#onContentSeeked = this.#handleContentSeeked.bind(this);
    this.#onContentVolumeChange = this.#handleContentVolumeChange.bind(this);
    this.#onWindowResize = this.#handleWindowResize.bind(this);
    this.#onAdsManagerLoaded = this.#handleAdsManagerLoaded.bind(this);
    this.#onAdsLoaderError = this.#handleAdsLoaderError.bind(this);
    this.#onAdsManagerError = this.#handleAdsManagerError.bind(this);
    this.#onAdEvent = this.#handleAdEvent.bind(this);
    this.#onVisibilityChange = this.#handleVisibilityChange.bind(this);

    const { overlay, container } = this.#createOverlay();
    this.#adOverlay = overlay;
    this.#adContainer = container;
  }

  configure(options: AdsControllerConfig = {}): void {
    if (
      typeof options.adTimeout === 'number' &&
      Number.isFinite(options.adTimeout)
    ) {
      this.#adTimeout = options.adTimeout;
    }

    if (Object.hasOwn(options, 'adTagSrc')) {
      this.setTagSource(options.adTagSrc ?? null);
    }

    if (Object.hasOwn(options, 'contentSrc')) {
      this.setContentSource(options.contentSrc ?? null);
    }
  }

  setContentSource(contentSrc: string | null): void {
    const normalized = contentSrc || null;
    if (normalized === this.#contentSrc) return;

    this.#contentSrc = normalized;

    if (!this.#shouldUseIma()) {
      this.#applyContentSourceIfNeeded();
    }
  }

  setTagSource(adTagSrc: string | null): void {
    const normalized = adTagSrc || null;
    if (normalized === this.#adTagSrc) return;

    this.#adTagSrc = normalized;
    this.#resetAdState();

    if (!this.#shouldUseIma()) {
      this.#applyContentSourceIfNeeded();
      return;
    }

    if (this.#connected) {
      if (this.#shouldAutoplayContent()) {
        this.#requestAutoplay();
      } else {
        this.#primeAdsInBackground();
      }
    }
  }

  connect(): void {
    if (this.#connected) return;
    this.#connected = true;
    this.#controllerEl = typeof this.host.closest === 'function'
      ? this.host.closest('media-controller') as HTMLElement | null
      : null;

    const playTarget = this.#controllerEl || this.host;
    playTarget.addEventListener(
      MEDIA_PLAY_REQUEST,
      this.#onMediaPlayRequest,
      { capture: true },
    );
    this.host.addEventListener('play', this.#onContentPlay);
    this.host.addEventListener('playing', this.#onContentPlaying);
    this.host.addEventListener('ended', this.#onContentEnded);
    this.host.addEventListener('seeking', this.#onContentSeeking);
    this.host.addEventListener('seeked', this.#onContentSeeked);
    this.host.addEventListener('volumechange', this.#onContentVolumeChange);
    globalThis.addEventListener('resize', this.#onWindowResize);
    document.addEventListener('fullscreenchange', this.#onWindowResize);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    this.#ensureOverlayMounted();
    if (this.#shouldUseIma()) {
      if (this.#shouldAutoplayContent()) {
        this.#requestAutoplay();
      } else {
        this.#primeAdsInBackground();
      }
    } else {
      this.#applyContentSourceIfNeeded();
    }
  }

  disconnect(): void {
    if (!this.#connected) return;
    this.#connected = false;

    const playTarget = this.#controllerEl || this.host;
    playTarget.removeEventListener(
      MEDIA_PLAY_REQUEST,
      this.#onMediaPlayRequest,
      { capture: true },
    );
    this.host.removeEventListener('play', this.#onContentPlay);
    this.host.removeEventListener('playing', this.#onContentPlaying);
    this.host.removeEventListener('ended', this.#onContentEnded);
    this.host.removeEventListener('seeking', this.#onContentSeeking);
    this.host.removeEventListener('seeked', this.#onContentSeeked);
    this.host.removeEventListener('volumechange', this.#onContentVolumeChange);
    globalThis.removeEventListener('resize', this.#onWindowResize);
    document.removeEventListener('fullscreenchange', this.#onWindowResize);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);

    this.#controllerEl = null;
    this.#teardownIma({ keepLoader: false });
    this.#hideOverlay();
  }

  requestContentPlay(
    allowUnavailableImaBypass = true,
    preserveUserGesture = true,
  ): Promise<unknown> {
    if (!this.#shouldUseIma()) {
      this.#applyContentSourceIfNeeded();
      return this.host._playContentInternal();
    }

    if (this.#adsBypassedForSession) {
      this.#applyContentSourceIfNeeded();
      return this.#resumeContentPlayback({
        preserveUserGesture,
      });
    }

    if (this.#playRequestPromise) {
      return this.#playRequestPromise;
    }

    // Keep first-click playback simple: if the SDK is not ready at the moment
    // the user asks to play, fail open to content immediately instead of
    // blocking the interaction on IMA loading.
    if (allowUnavailableImaBypass && !getIma()) {
      this.#bypassAdsForSession();
      return this.#resumeContentPlayback({
        preserveUserGesture,
      });
    }

    // Never hold a real user click behind async ad priming. If the ads manager
    // is not already ready by click time, fail open immediately and preserve
    // the gesture for content playback instead of forcing a second click.
    if (preserveUserGesture && !this.#adsManager) {
      this.#bypassAdsForSession();
      return this.#resumeContentPlayback({
        preserveUserGesture,
      });
    }

    // On iOS, AdDisplayContainer.initialize() must be called within the user
    // gesture call-stack.  If the IMA SDK is already loaded (the common path –
    // connect() kicks off primeAdsInBackground() which loads it), we create the
    // AdsLoader + AdDisplayContainer and initialize synchronously *before* any
    // await so the gesture is preserved.
    if (!this.#adDisplayInitialized && getIma()) {
      try {
        this.#ensureAdsLoader();
      } catch (error) {
        // Will be retried in the async path below — surface as a warning so
        // ad-ops can diagnose if the async retry also fails.
        console.warn('[AdTag] synchronous ensureAdsLoader failed:', error);
      }
      this.#initializeAdDisplayContainer();
    }

    this.#playRequestPromise = (async () => {
      try {
        await this.#primeAds();
      } catch (error) {
        this.#clearPrimedAdsState();
        this.#dispatchAdError(error, {});
        this.#applyContentSourceIfNeeded();
        return await this.#resumeContentPlayback({
          preserveUserGesture,
        });
      }

      // Re-attempt in case the container was not yet created above (cold start).
      this.#initializeAdDisplayContainer();

      if (!this.#adsManager || this.#adsCompleted) {
        this.#applyContentSourceIfNeeded();
        return await this.#resumeContentPlayback({
          preserveUserGesture,
        });
      }

      if (!this.#adsManagerStarted) {
        // Simple VAST tags return [] from getCuePoints() even though they have
        // a preroll.  Only treat as "no preroll" when IMA has reported actual
        // cue points AND none of them is at position 0 (VMAP-only midrolls /
        // postrolls).  An empty cue-points array means "unknown" — suppress
        // content and wait for IMA to tell us when to resume via
        // CONTENT_RESUME_REQUESTED.
        const knownNoPreroll = this.#cuePoints.length > 0 &&
          !this.#hasPrerollCuePoint();
        this.#suppressContentPlayback = !knownNoPreroll;
        this.#requestContentResumeAfterBreak = !knownNoPreroll;
        if (knownNoPreroll) {
          this.#applyContentSourceIfNeeded();
        }
        this.#startAdsManager();
        if (!knownNoPreroll) {
          return;
        }
      }

      this.#applyContentSourceIfNeeded();
      return await this.#resumeContentPlayback({
        preserveUserGesture,
      });
    })().finally(() => {
      this.#playRequestPromise = null;
    });

    return this.#playRequestPromise;
  }

  #shouldUseIma(): boolean {
    return Boolean(this.#adTagSrc) && !this.#isLiveContent();
  }

  #isLiveContent(): boolean {
    if (this.host.hasAttribute?.('live-status-url')) return true;
    return this.#controllerEl?.dataset?.liveStream === 'true';
  }

  async #primeAds(): Promise<void> {
    if (!this.#shouldUseIma()) return;
    if (this.#adsBypassedForSession) return;
    if (!this.#imaReadyPromise) {
      this.#imaReadyPromise = (async () => {
        try {
          await loadImaSdk();
          if (this.#adsBypassedForSession) return;
          this.#ensureAdsLoader();
          if (this.#adsBypassedForSession) {
            this.#teardownIma({ keepLoader: false });
            return;
          }
          await this.#requestAds();
        } catch (error) {
          this.#clearPrimedAdsState();
          throw error;
        }
      })();
    }

    await this.#imaReadyPromise;
  }

  #primeAdsInBackground(): void {
    void this.#primeAds().catch(() => {
      // Loader failures are surfaced through `adserror` and fail-open playback.
    });
  }

  #requestAutoplay(): void {
    if (this.#playRequestPromise || this.#hostIsPlaying()) return;

    if (!this.#canAttemptAutoplayWithoutGesture()) {
      this.#primeAdsInBackground();
      return;
    }

    if (!getIma()) {
      void this.#primeAds().then(() => {
        if (this.#playRequestPromise || this.#hostIsPlaying()) return;
        void this.requestContentPlay(false, false).catch((error) => {
          if (this.#shouldIgnoreAutoplayError(error)) {
            return;
          }

          this.#dispatchAdError(error, {});
          this.#applyContentSourceIfNeeded();
        });
      }).catch((error) => {
        if (this.#shouldIgnoreAutoplayError(error)) {
          return;
        }

        this.#dispatchAdError(error, {});
        this.#applyContentSourceIfNeeded();
      });
      return;
    }

    void this.requestContentPlay(false, false).catch((error) => {
      if (this.#shouldIgnoreAutoplayError(error)) {
        return;
      }

      this.#dispatchAdError(error, {});
      this.#applyContentSourceIfNeeded();
    });
  }

  #ensureAdsLoader(): void {
    if (this.#adsLoader) return;

    const ima = getIma();
    if (!ima) {
      throw new Error('IMA SDK is not available');
    }

    ima.settings?.setVpaidMode?.(ima.ImaSdkSettings?.VpaidMode?.DISABLED);
    ima.settings?.setPlayerType?.('bunny-stream-player');

    this.#ensureOverlayMounted();
    this.#adDisplayContainer = new ima.AdDisplayContainer(
      this.#adContainer,
      this.host.nativeEl || this.host,
    );
    this.#adsLoader = new ima.AdsLoader(this.#adDisplayContainer);
    this.#adsLoader.addEventListener(
      ima.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
      this.#onAdsManagerLoaded,
    );
    this.#adsLoader.addEventListener(
      ima.AdErrorEvent.Type.AD_ERROR,
      this.#onAdsLoaderError,
    );
  }

  #requestAds(): Promise<ImaAdsManager> {
    if (this.#adsRequestPromise) {
      return this.#adsRequestPromise;
    }

    this.#adsRequestPromise = new Promise<ImaAdsManager>((resolve, reject) => {
      try {
        const ima = getIma();
        if (!ima || !this.#adsLoader || !this.#adTagSrc) {
          throw new Error('IMA AdsLoader is not ready');
        }

        const request = new ima.AdsRequest();
        request.adTagUrl = this.#adTagSrc;

        const { width, height } = this.#getSlotDimensions();
        request.linearAdSlotWidth = width;
        request.linearAdSlotHeight = height;
        request.nonLinearAdSlotWidth = width;
        request.nonLinearAdSlotHeight = Math.max(1, Math.round(height / 3));
        request.vastLoadTimeout = this.#adTimeout;

        request.setAdWillAutoPlay?.(Boolean(this.host.autoplay));
        request.setAdWillPlayMuted?.(Boolean(this.host.muted));

        this.#adsRequestResolve = resolve;
        this.#adsRequestReject = reject;
        this.#adsLoader.requestAds(request);
      } catch (error) {
        reject(error);
      }
    });

    return this.#adsRequestPromise;
  }

  #handleAdsManagerLoaded(event: unknown): void {
    if (this.#adsBypassedForSession) {
      this.#adsRequestResolve?.(null as never);
      this.#adsRequestResolve = null;
      this.#adsRequestReject = null;
      this.#clearPrimedAdsState();
      this.#teardownIma({ keepLoader: false });
      return;
    }

    try {
      const ima = getIma();
      if (!ima) {
        throw new Error('IMA SDK is not available');
      }

      const settings = new ima.AdsRenderingSettings();
      settings.restoreCustomPlaybackStateOnAdBreakComplete = true;

      const managerLoadedEvent = event as ImaAdsManagerLoadedEvent;
      // Always pass the native video element (not a custom playback object).
      // On mobile (iPhone), IMA reuses this element for ad playback and
      // custom playback mode prevents IMA from controlling it directly.
      const adsManager = managerLoadedEvent.getAdsManager(
        this.host.nativeEl || this.host,
        settings,
      );

      this.#adsManager = adsManager;
      this.#attachAdsManagerListeners();
      this.#cuePoints = normalizeCuePoints(this.#adsManager.getCuePoints?.());
      this.#emitCuePointsChanged();

      // Same "known no-preroll" logic as requestContentPlay(): only apply
      // content source eagerly when VMAP reports cue points and none is at 0.
      // Simple VAST (empty cue points) always has a preroll — don't apply
      // content source here; wait for CONTENT_RESUME_REQUESTED.
      const knownNoPreroll = this.#cuePoints.length > 0 &&
        !this.#hasPrerollCuePoint();
      if (knownNoPreroll) {
        this.#startAdsManagerForAutoplayIfNeeded();
        this.#applyContentSourceIfNeeded();
      }

      this.#adsRequestResolve?.(adsManager);
    } catch (error) {
      this.#adsRequestReject?.(error);
      this.#handleLoaderFailure(error);
    } finally {
      this.#adsRequestResolve = null;
      this.#adsRequestReject = null;
    }
  }

  #handleAdsLoaderError(event: unknown): void {
    const normalized = normalizeAdError(event);
    this.#adsRequestReject?.(normalized.error);
    this.#adsRequestResolve = null;
    this.#adsRequestReject = null;
    this.#handleLoaderFailure(normalized.error, normalized);
  }

  #attachAdsManagerListeners(): void {
    if (!this.#adsManager) return;

    const ima = getIma();
    if (!ima) return;

    this.#adsManager.addEventListener(
      ima.AdErrorEvent.Type.AD_ERROR,
      this.#onAdsManagerError,
    );

    for (const type of buildAdsManagerEventTypes(ima)) {
      this.#adsManager.addEventListener(type, this.#onAdEvent);
    }
  }

  #removeAdsManagerListeners(): void {
    if (!this.#adsManager) return;

    const ima = getIma();
    if (!ima) return;

    this.#adsManager.removeEventListener(
      ima.AdErrorEvent.Type.AD_ERROR,
      this.#onAdsManagerError,
    );

    for (const type of buildAdsManagerEventTypes(ima)) {
      this.#adsManager.removeEventListener(type, this.#onAdEvent);
    }
  }

  #startAdsManager(): void {
    if (!this.#adsManager) return;

    const ima = getIma();
    if (!ima) {
      throw new Error('IMA SDK is not available');
    }

    const { width, height } = this.#getSlotDimensions();
    this.#adsManagerStarted = true;

    try {
      this.#adsManager.init(width, height, this.#getViewMode());
      this.#adsManager.start();
    } catch (error) {
      this.#adsManagerStarted = false;
      throw error;
    }
  }

  #initializeAdDisplayContainer(): void {
    if (this.#adDisplayInitialized || !this.#adDisplayContainer) return;
    this.#adDisplayContainer.initialize?.();
    this.#adDisplayInitialized = true;
  }

  #handleAdEvent(event: unknown): void {
    const ima = getIma();
    if (!ima) return;

    const adEvent = event as ImaAdEvent;
    switch (adEvent.type) {
      case ima.AdEvent.Type.CONTENT_PAUSE_REQUESTED:
        this.#handleContentPauseRequested(adEvent);
        break;
      case ima.AdEvent.Type.CONTENT_RESUME_REQUESTED:
        this.#handleContentResumeRequested();
        break;
      case ima.AdEvent.Type.ALL_ADS_COMPLETED:
        this.#adsCompleted = true;
        if (this.#adBreakActive) {
          this.#finishAdBreak({ resumeContent: false });
        }
        break;
      case ima.AdEvent.Type.PAUSED:
        this.#adPausedByClick = true;
        break;
      case ima.AdEvent.Type.CLICK:
        if (this.#adPausedByClick) {
          this.#adsManager?.resume?.();
        }
        break;
      case ima.AdEvent.Type.STARTED:
      case ima.AdEvent.Type.RESUMED:
        this.#adPausedByClick = false;
        break;
      default:
        break;
    }
  }

  #handleContentPauseRequested(event: ImaAdEvent): void {
    this.#adBreakActive = true;
    this.#suppressContentPlayback = true;
    this.#requestContentResumeAfterBreak = true;

    const podInfo = extractAdPodInfo(event);
    const cuePoint = podInfo.cuePoint ??
      (this.host.ended ? -1 : this.#getContentCurrentTime());

    this.#currentBreakDetail = {
      cuePoint,
      breakType: cuePointToBreakType(cuePoint),
      podIndex: podInfo.podIndex,
      adPosition: podInfo.adPosition,
      totalAds: podInfo.totalAds,
    };

    this.host.nativeEl?.pause?.();
    // Snapshot content position so #resumeContentPlayback can restore it.
    // Prefer the cue point (authoritative for VMAP midrolls); fall back to
    // the last known content time for simple VAST.
    this.#contentTimeBeforeAdBreak = cuePoint > 0
      ? cuePoint
      : (Number.isFinite(this.#lastKnownContentTime)
        ? this.#lastKnownContentTime
        : null);
    // iOS only: IMA uses custom playback (same <video> element) for ads, so
    // HLS.js's MediaSource has to be torn down – removing src triggers
    // hls-video-element's load(), which destroys the HLS.js instance. On
    // desktop IMA renders ads in its own container; detaching there would
    // reset playback at every midroll/postroll.
    if (isIOS() && this.host.getAttribute?.('src')) {
      this.host.removeAttribute('src');
    }
    this.#syncAdVolume();
    this.#showOverlay();
    this.host.dispatchEvent(
      new CustomEvent<AdBreakDetail>('adsbreakstart', {
        detail: this.#currentBreakDetail,
      }),
    );
    this.host.dispatchEvent(new CustomEvent('adsstart'));
  }

  #handleContentResumeRequested(): void {
    // IMA is telling us to resume content — always obey.  On iPhone the
    // event order can be allAdsCompleted → contentResumeRequested, and
    // allAdsCompleted's finishAdBreak(resumeContent:false) resets flags.
    // #finishAdBreak still guards on #contentCompleteFired internally.
    this.#finishAdBreak({ resumeContent: true });
  }

  #finishAdBreak(options: { resumeContent?: boolean } = {}): void {
    const breakDetail = this.#currentBreakDetail;
    const shouldResume = options.resumeContent === true &&
      !this.#contentCompleteFired;

    this.#adBreakActive = false;
    this.#adPausedByClick = false;
    this.#currentBreakDetail = null;
    this.#requestContentResumeAfterBreak = false;
    this.#suppressContentPlayback = !shouldResume;
    this.#hideOverlay();

    if (breakDetail) {
      this.host.dispatchEvent(new CustomEvent('adsend'));
      this.host.dispatchEvent(
        new CustomEvent<AdBreakDetail>('adsbreakend', {
          detail: breakDetail,
        }),
      );
    }

    if (shouldResume) {
      this.#applyContentSourceIfNeeded();
      void this.#resumeContentPlayback().catch((error) => {
        this.#dispatchAdError(error, {});
      });
    }
  }

  #handleAdsManagerError(event: unknown): void {
    const normalized = normalizeAdError(event);

    if (this.#shouldRetryAdsAfterAutoplayPermissionFailure(normalized)) {
      this.#resetAutoplayAdAttempt();
      return;
    }

    this.#dispatchAdError(normalized.error, {
      errorCode: normalized.errorCode,
      vastErrorCode: normalized.vastErrorCode,
    });

    if (this.#adBreakActive) {
      try {
        this.#adsManager?.discardAdBreak?.();
      } catch (_error) {
        // Best-effort; finishAdBreak below handles cleanup regardless.
      }
    }

    this.#finishAdBreak({
      resumeContent: !this.#contentCompleteFired,
    });
  }

  #handleLoaderFailure(
    error: unknown,
    detail: Partial<AdErrorPayload> = {},
  ): void {
    this.#clearPrimedAdsState();
    this.#teardownIma({ keepLoader: false });
    this.#cuePoints = [];
    this.#suppressContentPlayback = false;
    this.#emitCuePointsChanged();
    this.#dispatchAdError(error, detail);
    this.#applyContentSourceIfNeeded();
  }

  #startAdsManagerForAutoplayIfNeeded(): void {
    if (!this.#shouldAutoplayContent()) return;
    if (
      this.#playRequestPromise || this.#adsManagerStarted ||
      this.#hostIsPlaying()
    ) {
      return;
    }

    try {
      this.#initializeAdDisplayContainer();
      this.#startAdsManager();
    } catch (error) {
      this.#dispatchAdError(error, {});
    }
  }

  #handleMediaPlayRequest(event: Event): void {
    if (!this.#shouldUseIma()) return;
    if (this.host._isPlayBypassed?.()) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    void this.requestContentPlay().catch((error) => {
      this.#dispatchAdError(error, {});
      this.#applyContentSourceIfNeeded();
      void this.host._playContentBypass();
    });
  }

  #handleContentPlay(): void {
    if (!this.#shouldUseIma()) return;
    if (this.host._consumePlayBypass?.()) return;

    // During an active ad break, IMA may use the content video element for
    // ad playback (e.g., on mobile/iPhone).  Do not pause – let IMA control
    // the element.
    if (this.#adBreakActive) return;

    if (this.#suppressContentPlayback) {
      this.host.nativeEl?.pause?.();
      return;
    }

    if (!this.#adsManagerStarted) {
      this.host.nativeEl?.pause?.();
      if (this.#playRequestPromise) return;

      void this.requestContentPlay().catch((error) => {
        this.#dispatchAdError(error, {});
        this.#applyContentSourceIfNeeded();
        void this.host._playContentBypass();
      });
    }
  }

  #handleContentPlaying(): void {
    // During an active ad break, IMA may use the content video element for
    // ad playback on mobile.  Do not pause or update content position.
    if (this.#adBreakActive) return;

    if (this.#suppressContentPlayback) {
      this.host.nativeEl?.pause?.();
      return;
    }

    this.#lastKnownContentTime = this.#getContentCurrentTime();
  }

  #handleContentEnded(): void {
    // During an active ad break on mobile, IMA plays ads on the content
    // video element.  The 'ended' event is from the ad, not the content.
    if (this.#adBreakActive) return;

    this.#contentCompleteFired = true;
    if (!this.#shouldUseIma() || !this.#adsLoader) return;
    this.#adsLoader.contentComplete?.();
  }

  #handleContentSeeking(): void {
    if (this.#adBreakActive) return;
    this.#isSeeking = true;
    this.#seekSnapshotTime = this.#lastKnownContentTime;
  }

  #handleContentSeeked(): void {
    if (this.#adBreakActive) return;
    this.#isSeeking = false;
    this.#lastKnownContentTime = this.#getContentCurrentTime();
  }

  #handleContentVolumeChange(): void {
    this.#syncAdVolume();
  }

  #syncAdVolume(): void {
    if (!this.#adsManager?.setVolume) return;

    const volume = this.host.muted ? 0 : (this.host.volume ?? 1);
    this.#adsManager.setVolume(volume);
  }

  #handleWindowResize(): void {
    if (!this.#adsManagerStarted || !this.#adsManager?.resize) return;

    const { width, height } = this.#getSlotDimensions();
    this.#adsManager.resize(width, height, this.#getViewMode());
  }

  #handleVisibilityChange(): void {
    if (document.hidden) return;
    if (!this.#adBreakActive || !this.#adPausedByClick) return;

    this.#adsManager?.resume?.();
  }

  #hasPrerollCuePoint(): boolean {
    return this.#cuePoints.some((cuePoint) => cuePoint === 0);
  }

  #emitCuePointsChanged(): void {
    this.host.dispatchEvent(
      new CustomEvent<CuePointsDetail>('adcuepointschange', {
        detail: {
          cuePoints: [...this.#cuePoints],
        },
      }),
    );
  }

  #dispatchAdError(
    error: unknown,
    detail: Partial<AdErrorPayload> = {},
  ): void {
    if (this.#shouldIgnoreContentAbort(error)) {
      return;
    }

    const normalized = normalizeAdError(error);
    console.warn('Ad playback failed', {
      message: normalized.message,
      errorCode: normalized.errorCode,
      vastErrorCode: normalized.vastErrorCode,
      innerError: normalized.innerError?.message || null,
      detail,
    });
    this.host.dispatchEvent(
      new CustomEvent<AdErrorPayload>('adserror', {
        detail: {
          message: normalized.message,
          errorCode: normalized.errorCode,
          vastErrorCode: normalized.vastErrorCode,
          ...detail,
        },
      }),
    );
  }

  #shouldIgnoreContentAbort(error: unknown): boolean {
    if (!(error instanceof Error) || error.name !== 'AbortError') {
      return false;
    }

    return this.#adBreakActive ||
      this.#suppressContentPlayback ||
      this.#requestContentResumeAfterBreak;
  }

  #shouldIgnoreAutoplayError(error: unknown): boolean {
    return error instanceof Error && error.name === 'NotAllowedError';
  }

  #shouldRetryAdsAfterAutoplayPermissionFailure(error: {
    message: string;
    errorCode: number | null;
    vastErrorCode: number | null;
  }): boolean {
    if (this.#adBreakActive) return false;
    if (!this.#shouldAutoplayContent()) return false;
    if (this.#hostIsPlaying()) return false;

    const message = error.message.toLowerCase();
    const looksLikeAutoplayPermissionError =
      message.includes('not allowed by the user agent') ||
      message.includes('user denied permission');

    return looksLikeAutoplayPermissionError ||
      (error.errorCode === 400 && error.vastErrorCode === 400);
  }

  #shouldAutoplayContent(): boolean {
    return this.host.autoplay === true ||
      this.host.hasAttribute?.('autoplay') === true;
  }

  #canAttemptAutoplayWithoutGesture(): boolean {
    return this.host.muted === true ||
      this.host.hasAttribute?.('muted') === true;
  }

  #hostIsPlaying(): boolean {
    const nativeEl = this.host.nativeEl;
    if (nativeEl) {
      return nativeEl.paused === false && nativeEl.ended === false;
    }

    return Boolean(
      (this.host as AdTagControllerHost & { paused?: boolean }).paused ===
        false,
    );
  }

  #applyContentSourceIfNeeded(): void {
    if (this.#isLiveContent()) return;
    if (!this.#contentSrc) return;

    if (this.host.getAttribute?.('src') !== this.#contentSrc) {
      this.host.setAttribute('src', this.#contentSrc);
    }
  }

  #bypassAdsForSession(): void {
    this.#adsBypassedForSession = true;
    this.#clearPrimedAdsState();
    this.#teardownIma({ keepLoader: false });
    if (this.#cuePoints.length > 0) {
      this.#cuePoints = [];
      this.#emitCuePointsChanged();
    }
    this.#adBreakActive = false;
    this.#adPausedByClick = false;
    this.#currentBreakDetail = null;
    this.#contentTimeBeforeAdBreak = null;
    this.#requestContentResumeAfterBreak = false;
    this.#suppressContentPlayback = false;
    this.#hideOverlay();
  }

  #resetAutoplayAdAttempt(): void {
    this.#clearPrimedAdsState();
    this.#teardownIma({ keepLoader: false });
    this.#adsManagerStarted = false;
    this.#adBreakActive = false;
    this.#adPausedByClick = false;
    this.#currentBreakDetail = null;
    this.#contentTimeBeforeAdBreak = null;
    this.#requestContentResumeAfterBreak = false;
    this.#suppressContentPlayback = false;
    if (this.#cuePoints.length > 0) {
      this.#cuePoints = [];
      this.#emitCuePointsChanged();
    }
    this.#hideOverlay();
  }

  #shouldRetryDeferredPlayback(error: unknown): boolean {
    return error instanceof Error && error.name === 'AbortError';
  }

  async #resumeContentPlayback(
    options: { preserveUserGesture?: boolean } = {},
  ): Promise<unknown> {
    this.#suppressContentPlayback = false;
    this.#applyContentSourceIfNeeded();

    if (options.preserveUserGesture === true) {
      try {
        this.#restoreContentTimeAfterAdBreak();
        return await this.host._playContentBypass();
      } catch (error) {
        if (!this.#shouldRetryDeferredPlayback(error)) {
          throw error;
        }
      }
    }

    // hls-video-element applies the source to the native <video> element
    // asynchronously (microtask yield in its load() method).  Wait for that
    // to complete before calling play(), otherwise play() fires on an element
    // with no source and silently fails.
    await Promise.resolve();

    this.#restoreContentTimeAfterAdBreak();
    return await this.host._playContentBypass();
  }

  #restoreContentTimeAfterAdBreak(): void {
    const target = this.#contentTimeBeforeAdBreak;
    this.#contentTimeBeforeAdBreak = null;
    if (target == null || !Number.isFinite(target) || target <= 0) return;
    if (this.#isLiveContent()) return;

    try {
      this.host.currentTime = target;
    } catch (_error) {
      // Some browsers throw if the source is not yet seekable; the
      // hls-video-element will apply the position once metadata loads.
    }
  }

  #getSlotDimensions(): { width: number; height: number } {
    const target = this.#controllerEl || this.host;
    const width = Math.max(
      1,
      Math.round(target?.clientWidth || this.host.clientWidth || 640),
    );
    const height = Math.max(
      1,
      Math.round(target?.clientHeight || this.host.clientHeight || 360),
    );

    return { width, height };
  }

  #getViewMode(): string {
    const ima = getIma();
    if (!ima) return 'normal';

    const fullscreenRoot = document.fullscreenElement;
    const playerRoot = this.#controllerEl || this.host;
    const isFullscreen = fullscreenRoot != null &&
      (fullscreenRoot === playerRoot ||
        (fullscreenRoot instanceof Element &&
          typeof fullscreenRoot.contains === 'function' &&
          fullscreenRoot.contains(playerRoot)));

    return isFullscreen ? ima.ViewMode.FULLSCREEN : ima.ViewMode.NORMAL;
  }

  #getContentCurrentTime(): number {
    try {
      return Number(this.host.currentTime || 0);
    } catch (_error) {
      return this.#lastKnownContentTime;
    }
  }

  #createOverlay(): { overlay: HTMLDivElement; container: HTMLDivElement } {
    const overlay = document.createElement('div');
    overlay.id = 'ima-ad-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = `
      position: absolute;
      inset: 0;
      z-index: 1000;
      visibility: hidden;
      opacity: 0;
      pointer-events: none;
      background: #000;
      transition: opacity 120ms linear;
    `;

    const container = document.createElement('div');
    container.id = 'ima-ad-container';
    container.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    `;

    overlay.appendChild(container);
    return { overlay, container };
  }

  #ensureOverlayMounted(): void {
    const parent = this.#controllerEl || this.host;
    if (!parent || this.#adOverlay.parentNode === parent) return;

    if (this.#adOverlay.isConnected) {
      this.#adOverlay.remove();
    }
    parent.appendChild(this.#adOverlay);
  }

  #showOverlay(): void {
    this.#ensureOverlayMounted();
    // On iPhone/iOS, IMA can reuse the native content <video> element for ad
    // playback instead of drawing into the separate ad container. Keep the
    // overlay transparent there so we do not cover the ad with our fallback
    // black background.
    this.#adOverlay.style.background = isIOS() ? 'transparent' : '#000';
    this.#adOverlay.style.visibility = 'visible';
    this.#adOverlay.style.opacity = '1';
    this.#adOverlay.style.pointerEvents = 'auto';
    this.#adOverlay.setAttribute('aria-hidden', 'false');
  }

  #hideOverlay(): void {
    this.#adOverlay.style.visibility = 'hidden';
    this.#adOverlay.style.opacity = '0';
    this.#adOverlay.style.pointerEvents = 'none';
    this.#adOverlay.setAttribute('aria-hidden', 'true');
  }

  #resetAdState(): void {
    this.#playRequestPromise = null;
    this.#clearPrimedAdsState();
    this.#adsManagerStarted = false;
    this.#adsCompleted = false;
    this.#adBreakActive = false;
    this.#adPausedByClick = false;
    this.#currentBreakDetail = null;
    this.#contentCompleteFired = false;
    this.#cuePoints = [];
    this.#adDisplayInitialized = false;
    this.#requestContentResumeAfterBreak = false;
    this.#suppressContentPlayback = false;
    this.#adsBypassedForSession = false;
    this.#teardownIma({ keepLoader: false });
    this.#hideOverlay();
  }

  #clearPrimedAdsState(): void {
    this.#imaReadyPromise = null;
    this.#adsRequestPromise = null;
    this.#adsRequestResolve = null;
    this.#adsRequestReject = null;
  }

  #teardownIma(options: { keepLoader?: boolean } = {}): void {
    this.#removeAdsManagerListeners();
    this.#adsManager?.destroy?.();
    this.#adsManager = null;

    if (options.keepLoader !== true) {
      this.#adsLoader?.destroy?.();
      this.#adsLoader = null;
      this.#adDisplayContainer?.destroy?.();
      this.#adDisplayContainer = null;
      this.#adDisplayInitialized = false;
    }
  }
}

export type { AdsControllerConfig, Constructor };
